import { Point, Position } from "geojson";
import { LngLatLike } from "mapbox-gl";
import { CustomGeoSource, mapActive } from "../utils/map-utils";
import { CustomSource } from "../models/CustomSource";
import { RootStore } from "../../shared/stores/RootStore";
import { MapMode } from "../../survey/stores/MapStore";

const pointColor = "#00a498";

export class SurveysMapSource {
  map: mapboxgl.Map;
  active = false;
  interactions: string[] = [];
  rootStore: RootStore;

  constructor(map: mapboxgl.Map, rootStore: RootStore) {
    this.map = map;
    this.rootStore = rootStore;

    if (mapActive(this.map) && !this.map.getSource(sourceName)) {
      this.map.addSource(sourceName, sourceConfig);
      this.map.addLayer(clusterLayerConfig);
      this.map.addLayer(clusterSymbolLayerConfig);
      this.map.addLayer(pointLayerConfig);
    }
  }

  onModeChange(mode: MapMode) {
    this.clearInteractions();

    if (mode === MapMode.HomePage) {
      this.configInteractions();
      this.updateMapData();
      this.active = true;
    } else {
      this.setMapData([]);
      this.active = false;
    }
  }

  clear() {
    this.clearInteractions();

    if (mapActive(this.map)) {
      try {
        this.map.removeLayer(clusterLayerConfig.id);
        this.map.removeLayer(clusterSymbolLayerConfig.id);
        this.map.removeLayer(pointLayerConfig.id);
        this.map.removeSource(sourceName);
      } catch (e) {
        console.warn("Error while removing layers and sources", e);
      }
    }
  }

  clearInteractions() {
    this.interactions.map((name) => this.map.removeInteraction(name));

    this.map.removeInteraction("click-cluster");
    this.map.removeInteraction("click-point");
  }

  eventSurveysUpdated() {
    if (!this.active) return;

    this.updateMapData();
  }

  private updateMapData() {
    const { surveyStore } = this.rootStore;

    this.setMapData(
      surveyStore.surveys.map((survey) => ({
        id: survey.id,
        type: "Feature",
        properties: {
          id: survey.id,
          name: survey.name,
        },
        geometry: {
          type: "Point",
          coordinates: [survey.longitude, survey.latitude],
        },
      })),
    );
  }

  private setMapData(features: GeoJSON.Feature[]) {
    if (mapActive(this.map)) {
      const source = this.map.getSource(sourceName) as mapboxgl.GeoJSONSource;
      source?.setData({
        type: "FeatureCollection",
        features,
      });
    }
  }

  configInteractions() {
    const { surveyStore } = this.rootStore;

    this.interactions.push("click-cluster");
    this.map.addInteraction("click-cluster", {
      type: "click",
      target: { layerId: clusterLayerName },
      handler: (e) => {
        const features = this.map.queryRenderedFeatures(e.point, {
          layers: [clusterLayerName],
        });
        const clusterId = features[0].properties!.cluster_id;
        const source = this.map.getSource(sourceName) as CustomGeoSource;

        const geometry = features[0].geometry as Point;
        const coordinates = geometry.coordinates.slice() as Position;
        const { x, y } = this.map.project(coordinates as LngLatLike);

        source.getClusterLeaves(clusterId, 100, 0, (err, leaves) => {
          if (leaves) {
            const surveyIds = leaves.map((f) => f.id as string);
            surveyStore.setMapSelection(x, y, surveyIds);
          }
        });
      },
    });

    this.interactions.push("click-point");
    this.map.addInteraction("click-point", {
      type: "click",
      target: { layerId: pointLayerName },
      handler: (e) => {
        const feature = e.feature as mapboxgl.GeoJSONFeature;
        const geometry = feature.geometry as Point;
        const coordinates = geometry.coordinates.slice() as Position;
        const surveyId = feature.properties!.id as string;
        const { x, y } = this.map.project(coordinates as LngLatLike);
        surveyStore.setMapSelection(x, y, [surveyId]);
      },
    });

    this.addHoverInteractions(clusterLayerName);
    this.addHoverInteractions(pointLayerName);
  }

  private addHoverInteractions(layerId: string) {
    const mouseEnterInteraction = `mouseenter-${layerId}`;
    this.interactions.push(mouseEnterInteraction);
    this.map.addInteraction(mouseEnterInteraction, {
      type: "mouseenter",
      target: { layerId },
      handler: () => {
        this.map.getCanvas().style.cursor = "pointer";
      },
    });

    const mouseLeaveInteraction = `mouseleave-${layerId}`;
    this.interactions.push(mouseLeaveInteraction);
    this.map.addInteraction(mouseLeaveInteraction, {
      type: "mouseleave",
      target: { layerId },
      handler: () => {
        this.map.getCanvas().style.cursor = "";
      },
    });
  }
}

const sourceName = CustomSource.Surveys;
const sourceConfig: mapboxgl.GeoJSONSourceSpecification = {
  type: "geojson",
  generateId: true,
  data: {
    type: "FeatureCollection",
    features: [],
  },
  cluster: true,
  clusterMaxZoom: 18,
  clusterRadius: 50,
};

const clusterLayerName = `${sourceName}-cluster`;
const clusterLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: clusterLayerName,
  type: "circle",
  source: sourceName,
  filter: ["has", "point_count"],
  paint: {
    "circle-color": pointColor,
    "circle-radius": ["step", ["get", "point_count"], 20, 100, 30, 750, 40],
    "circle-stroke-width": 1,
    "circle-stroke-color": "#000",
    "circle-opacity": 0.75,
    "circle-emissive-strength": 1,
  },
};

const clusterSymbolLayerName = `${sourceName}-text`;
const clusterSymbolLayerConfig: mapboxgl.SymbolLayerSpecification = {
  id: clusterSymbolLayerName,
  type: "symbol",
  source: sourceName,
  filter: ["has", "point_count"],
  layout: {
    "text-field": ["get", "point_count_abbreviated"],
    "text-font": ["Arial Unicode MS Bold"],
    "text-size": 14,
  },
  paint: {
    "text-color": "#ffffff",
  },
};

const pointLayerName = `${sourceName}-unclustered-point`;
const pointLayerConfig: mapboxgl.CircleLayerSpecification = {
  id: pointLayerName,
  type: "circle",
  source: sourceName,
  filter: ["!", ["has", "point_count"]],
  paint: {
    "circle-color": pointColor,
    "circle-radius": 8,
    "circle-stroke-width": 1,
    "circle-stroke-color": "#000",
    "circle-opacity": 0.75,
    "circle-emissive-strength": 1,
  },
};
